import { Component, inject, resource, signal } from '@angular/core';
import { form, FormField, submit } from '@angular/forms/signals';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { FormControlAutocompleteComponent } from '@components/form-control/autocomplete/autocomplete.component';
import { LeafletGroupComponent } from '@components/leaflet/group/group.component';
import { environment } from '@environment';
import { ItemListResponse, PlanktonType, searchResource } from '@models';

interface DialogData {
  expedition: string[];
  fieldNumber: string;
  from: string | null;
  gearType: string[];
  geojson: GeoJSON.FeatureCollection | null;
  lifeStage: string[];
  planktonType: string | null;
  q: string;
  scientificName: string[];
  subtaxa: boolean;
  until: string | null;
}

@Component({
  selector: 'app-plankton-occurrence-filter',
  templateUrl: './filter.component.html',
  styleUrls: ['./filter.component.scss'],
  imports: [
    FormControlAutocompleteComponent,
    FormField,
    LeafletGroupComponent,
    MatAutocompleteModule,
    MatButtonModule,
    MatCardModule,
    MatDatepickerModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
  ],
})
export class OccurrenceFilterComponent {
  dialogRef = inject<MatDialogRef<OccurrenceFilterComponent>>(MatDialogRef);
  dialogData = inject<DialogData>(MAT_DIALOG_DATA);

  filterForm = form(
    signal<DialogData>({
      expedition: this.dialogData.expedition || [],
      fieldNumber: this.dialogData.fieldNumber || '',
      from: this.dialogData.from || null,
      gearType: this.dialogData.gearType || [],
      geojson: this.dialogData.geojson || null,
      lifeStage: this.dialogData.lifeStage || [],
      planktonType: this.dialogData.planktonType || null,
      q: this.dialogData.q || '',
      scientificName: this.dialogData.scientificName || [],
      subtaxa: !!this.dialogData.subtaxa,
      until: this.dialogData.until || null,
    }),
  );

  submit(event: Event) {
    event.preventDefault();

    submit(this.filterForm, async () => {
      this.dialogRef.close({
        expedition: this.filterForm.expedition().value() || undefined,
        fieldNumber: this.filterForm.fieldNumber().value() || undefined,
        from: this.filterForm.from().value() || undefined,
        gearType: this.filterForm.gearType().value() || undefined,
        geojson: this.filterForm.geojson().value() || undefined,
        lifeStage: this.filterForm.lifeStage().value() || undefined,
        planktonType: this.filterForm.planktonType().value() || undefined,
        q: this.filterForm.q().value() || undefined,
        scientificName: this.filterForm.scientificName().value() || undefined,
        subtaxa: this.filterForm.subtaxa().value() || undefined,
        until: this.filterForm.until().value() || undefined,
      });
    });
  }

  onGeoSearch(geometry: GeoJSON.FeatureCollection | undefined) {
    this.filterForm.geojson().value.set(geometry || null);
  }

  // autocomplete

  searchResourceOptions = {
    parse: (resp: unknown) => (resp as ItemListResponse<string>).items,
  };

  expeditionSearch = signal('');
  expeditionResource = searchResource<string[]>(
    () =>
      this.expeditionSearch().length > 0
        ? `${environment.kinkoHost}/plankton/vocab/?source=expedition&take=10&q=${this.expeditionSearch()}`
        : null,
    500,
    this.searchResourceOptions,
  );

  gearTypeSearch = signal('');
  gearTypeResource = searchResource<string[]>(
    () =>
      this.gearTypeSearch().length > 0
        ? `${environment.kinkoHost}/plankton/vocab/?source=gearType&take=10&q=${this.gearTypeSearch()}`
        : null,
    500,
    this.searchResourceOptions,
  );

  lifeStageSearch = signal('');
  lifeStageResource = searchResource<string[]>(
    () =>
      this.lifeStageSearch().length > 0
        ? `${environment.kinkoHost}/plankton/vocab/?source=lifeStage&take=10&q=${this.lifeStageSearch()}`
        : null,
    500,
    this.searchResourceOptions,
  );

  scientificNameSearch = signal('');
  scientificNameResource = searchResource<string[]>(
    () =>
      this.scientificNameSearch().length > 0
        ? `${environment.kinkoHost}/plankton/vocab/?source=scientificName&take=50&q=${this.scientificNameSearch()}`
        : null,
    500,
    this.searchResourceOptions,
  );

  planktonTypeSearch = signal('');
  planktonTypeResource = resource({
    params: () => ({ q: this.planktonTypeSearch() }),
    loader: async (p) => {
      return PlanktonType.filter((v: string) => {
        const q = p.params.q.toLowerCase();
        return q.length === 0 || v.includes(q);
      });
    },
  });
}
